import { observer, useLocalObservable } from 'mobx-react-lite';
import { Box } from '@mui/material';
import './scss/create-group.style.scss';
import i18n from '../../i18n';
import ButtonComponent from '../../component/button/button.component';
import { CustomerLoading } from '../../component/loading/loading';
import { GroupState } from './group-management-state';
import DialogComponent from '../../component/dialog/dialog.component';
import GroupAccount, { getGroupArchiveSections } from './group-info/group-account';
import { GroupAccountState } from './group-info/group-account-state';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { groupUnitData } from './group-unit';
import {
    ArchiveSaveProgressBar,
    calculateArchiveFormProgress,
} from '../../component/archive-form';

/**
 * 新建集团弹窗内容
 */
const CreateGroupDialog = observer(({ useGroupData }: { useGroupData: GroupState }) => {

    useTranslation()
    // 解构数据
    const { isLoading, isOpenDialog } = useGroupData;

    // 集团账号新增数据管理
    const useGroupAccountData = useLocalObservable(() => new GroupAccountState({
        componentType: 'ADD',
    }));

    useEffect(() => {
        if (isOpenDialog) {
            useGroupAccountData.init();
        }
    }, [isOpenDialog])

    // 新增集团表单完成度，与工作台导航使用同一套分区规则
    const archiveProgress = calculateArchiveFormProgress(
        getGroupArchiveSections('ADD'),
        useGroupAccountData.groupEditDetail as Record<string, unknown>,
    );

    /**
     * 保存新增集团，并沿用原有关闭、刷新和进入详情行为。
     */
    const handleSave = async () => {
        useGroupData.load();
        // 新增结果用于成功后进入刚创建的集团详情
        const { successFlag, group } = await useGroupAccountData.onSave('ADD');
        useGroupData.loaded();
        if (successFlag) {
            useGroupAccountData.initGroupData();
            useGroupData.closeAddGroupDialog();
            await useGroupData.getGroupListData();
            useGroupData.showUnit = true;
            useGroupData.enterGroupDetailObj.groupData = group;
            groupUnitData.updateGroupId(group);
            groupUnitData.updatePanel(0);
        }
    };

    /**
     * 保存当前集团并清空表单，保留弹窗以继续新增。
     */
    const handleSaveAndContinue = async () => {
        useGroupData.load();
        // 成功标识决定是否清空当前表单并刷新集团列表
        const { successFlag } = await useGroupAccountData.onSave('ADD');
        useGroupData.loaded();
        if (successFlag) {
            useGroupAccountData.initGroupData();
            await useGroupData.getGroupListData();
        }
    };

    // *********************
    // View
    // *********************
    return (
        <DialogComponent
            className='create-group-dialog'
            fullWidth
            defaultHeaderProps={{
                title: i18n.t('create_group_component_text_create_group'),
            }}
            maxWidth={'lg'}
            open={isOpenDialog}
            // 新建集团的锚点紧贴滚动容器顶部，避免滚动内容从默认的 8px 内边距中透出。
            dialogContentStyle={{ paddingTop: 0 }}
            onClose={() => {
                // 关闭弹窗
                useGroupData.closeAddGroupDialog();
                // 清空表单
                useGroupAccountData.initGroupData();
            }}
            transitionDuration={{
                appear: 450,
                enter: 400,
                exit: 300
            }}
            footer={(
                <ArchiveSaveProgressBar
                    progress={archiveProgress}
                    disabled={isLoading}
                    actions={(
                        <>
                            <ButtonComponent
                                size='small'
                                variant='contained'
                                iconBtn={false}
                                titleText={i18n.t('general_text_save')}
                                handleClick={handleSave}
                            >
                                {i18n.t('general_text_save')}
                            </ButtonComponent>
                            <ButtonComponent
                                size='small'
                                variant='outlined'
                                iconBtn={false}
                                titleText={i18n.t('general_text_save_and_update')}
                                handleClick={handleSaveAndContinue}
                            >
                                {i18n.t('general_text_save_and_update')}
                            </ButtonComponent>
                        </>
                    )}
                />
            )}
        >
            {/* 新增集团表单内容 */}
            <Box className='createGroup-modal'>
                {
                    isLoading &&
                    <CustomerLoading />
                }
                {/* 集团账号视图 */}
                <GroupAccount componentType='ADD' useGroupAccountMobx={useGroupAccountData} />
            </Box>
        </DialogComponent>
    )
})

export default CreateGroupDialog;

